import React, { useState, useEffect } from 'react';
import { Modal } from '../../../components/common/Modal';
import { Button } from '../../../components/common/Button';
import { FormField, Input, Select, Textarea } from '../../../components/common/FormField';

const EMPTY = { name:'', company:'', email:'', phone:'', website:'', address:{street:'',city:'',state:'',country:'India',postalCode:''}, gstin:'', pan:'', bankDetails:{accountNo:'',bankName:'',ifsc:''}, creditLimit:'', notes:'', status:'active', tags:'' };

export function ContactForm({ isOpen, onClose, onSave, editing, defaultType }) {
  const [form, setForm]   = useState(EMPTY);
  const [tab, setTab]     = useState('basic');
  const [saving, setSv]   = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTab('basic');
    if (editing) {
      setForm({
        name: editing.name||'', company: editing.company||'', email: editing.email||'',
        phone: editing.phone||'', website: editing.website||'',
        address: editing.address||EMPTY.address,
        gstin: editing.gstin||'', pan: editing.pan||'',
        bankDetails: editing.bankDetails||EMPTY.bankDetails,
        creditLimit: editing.creditLimit||'', notes: editing.notes||'',
        status: editing.status||'active', tags: (editing.tags||[]).join(', '),
      });
    } else {
      setForm(EMPTY);
    }
  }, [editing, isOpen]);

  const s    = (k,v) => setForm(f => ({...f, [k]: v}));
  const sA   = (k,v) => setForm(f => ({...f, address: {...f.address, [k]: v}}));
  const sB   = (k,v) => setForm(f => ({...f, bankDetails: {...f.bankDetails, [k]: v}}));

  const submit = async e => {
    e.preventDefault(); setSv(true);
    try {
      const payload = { ...form, type: defaultType, creditLimit: form.creditLimit ? Number(form.creditLimit) : 0, tags: form.tags ? form.tags.split(',').map(t=>t.trim()).filter(Boolean) : [] };
      await onSave(payload);
    } finally { setSv(false); }
  };

  const TABS = [{id:'basic',label:'Basic'},{id:'address',label:'Address'},{id:'finance',label:'GST / Finance'},{id:'bank',label:'Bank'}];
  const label = defaultType ? (defaultType.charAt(0).toUpperCase() + defaultType.slice(1)) : 'Contact';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={(editing?'Edit ':'Add ') + label} size="lg">
      <form onSubmit={submit}>
        {/* Tabs */}
        <div className="flex border-b border-gray-100 mb-5 -mt-2 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} type="button" onClick={()=>setTab(t.id)}
              className={'px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ' + (tab===t.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Basic */}
        {tab==='basic' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Full Name" required><Input value={form.name} onChange={e=>s('name',e.target.value)} required placeholder="Rajesh Kumar"/></FormField>
              <FormField label="Company"><Input value={form.company} onChange={e=>s('company',e.target.value)} placeholder="Acme Pvt Ltd"/></FormField>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Email"><Input type="email" value={form.email} onChange={e=>s('email',e.target.value)} placeholder="email@example.com"/></FormField>
              <FormField label="Phone"><Input value={form.phone} onChange={e=>s('phone',e.target.value)} placeholder="+91 98765 43210"/></FormField>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Website"><Input value={form.website} onChange={e=>s('website',e.target.value)} placeholder="https://example.com"/></FormField>
              <FormField label="Status"><Select value={form.status} onChange={e=>s('status',e.target.value)}><option value="active">Active</option><option value="inactive">Inactive</option><option value="prospect">Prospect</option></Select></FormField>
            </div>
            <FormField label="Notes"><Textarea value={form.notes} onChange={e=>s('notes',e.target.value)} rows={2} placeholder="Internal notes..."/></FormField>
            <FormField label="Tags" hint="Comma separated: VIP, wholesale"><Input value={form.tags} onChange={e=>s('tags',e.target.value)} placeholder="VIP, regular"/></FormField>
          </div>
        )}

        {/* Address */}
        {tab==='address' && (
          <div className="space-y-4">
            <FormField label="Street"><Input value={form.address.street} onChange={e=>sA('street',e.target.value)} placeholder="123 MG Road"/></FormField>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="City"><Input value={form.address.city} onChange={e=>sA('city',e.target.value)} placeholder="Mumbai"/></FormField>
              <FormField label="State"><Input value={form.address.state} onChange={e=>sA('state',e.target.value)} placeholder="Maharashtra"/></FormField>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Pin Code"><Input value={form.address.postalCode} onChange={e=>sA('postalCode',e.target.value)} placeholder="400001"/></FormField>
              <FormField label="Country"><Input value={form.address.country} onChange={e=>sA('country',e.target.value)} placeholder="India"/></FormField>
            </div>
          </div>
        )}

        {/* Finance */}
        {tab==='finance' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="GSTIN" hint="15-digit GST number"><Input value={form.gstin} onChange={e=>s('gstin',e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" className="font-mono tracking-wider" maxLength={15}/></FormField>
              <FormField label="PAN" hint="10-char PAN"><Input value={form.pan} onChange={e=>s('pan',e.target.value.toUpperCase())} placeholder="AAAPL1234C" className="font-mono tracking-wider" maxLength={10}/></FormField>
            </div>
            <FormField label="Credit Limit (₹)"><Input type="number" value={form.creditLimit} onChange={e=>s('creditLimit',e.target.value)} placeholder="50000" min="0"/></FormField>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-amber-700">
              <p className="font-semibold mb-1">GST Number Format</p>
              <p className="text-xs">State(2) + PAN(10) + Entity(1) + Z + Check(1) = 15 chars</p>
              <p className="text-xs mt-1 font-mono">Example: 27AABCU9603R1ZX</p>
            </div>
          </div>
        )}

        {/* Bank */}
        {tab==='bank' && (
          <div className="space-y-4">
            <FormField label="Bank Name"><Input value={form.bankDetails.bankName} onChange={e=>sB('bankName',e.target.value)} placeholder="State Bank of India"/></FormField>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Account Number"><Input value={form.bankDetails.accountNo} onChange={e=>sB('accountNo',e.target.value)} placeholder="000001234567" className="font-mono"/></FormField>
              <FormField label="IFSC Code"><Input value={form.bankDetails.ifsc} onChange={e=>sB('ifsc',e.target.value.toUpperCase())} placeholder="SBIN0001234" className="font-mono tracking-wider" maxLength={11}/></FormField>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
              <p className="font-semibold mb-1">IFSC Format</p>
              <p className="text-xs">Bank(4) + 0 + Branch(6) = 11 chars · Example: <span className="font-mono">SBIN0001234</span></p>
            </div>
          </div>
        )}

        <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
          <Button type="submit" loading={saving} className="flex-1">{editing?'Update':'Add'} {label}</Button>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
}